'use client';

import posthog from 'posthog-js';
import { safeAnalyticsProperties, GROWTH_VERSION } from './growth';
import {
  POSTHOG_SIGNUP_DISTINCT_ID_COOKIE,
  POSTHOG_SIGNUP_DISTINCT_ID_COOKIE_MAX_AGE_SECONDS,
  isValidPostHogDistinctId,
} from '@/lib/posthog-signup';

type ClientAnalyticsProperties = Record<string, boolean | null | number | string | undefined>;
const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN || process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

function getPostHogConfig() {
  return {
    api_host: posthogHost,
    autocapture: false,
    capture_pageview: false,
    disable_surveys: true,
    // Keep anonymous visitor pageviews and referral traffic visible in PostHog.
    person_profiles: 'always' as const,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
  };
}

export function ensurePostHogInitialized() {
  if (typeof window === 'undefined' || !posthogKey) {
    return false;
  }

  if (!posthog.__loaded) {
    posthog.init(posthogKey, getPostHogConfig());
  } else {
    posthog.set_config(getPostHogConfig());
  }

  return true;
}

export function trackClientEvent(name: string, properties: ClientAnalyticsProperties = {}) {
  if (!ensurePostHogInitialized()) {
    return;
  }

  const cleanProperties = Object.fromEntries(Object.entries(safeAnalyticsProperties(properties))) as Record<
    string,
    boolean | null | number | string
  >;

  posthog.capture(name, {
    ...cleanProperties,
    analytics_schema_version: 2,
    release_version: GROWTH_VERSION,
    account_id: posthog.get_property('$user_id') || undefined,
  });
}

export function getCurrentPostHogDistinctId() {
  if (!ensurePostHogInitialized()) {
    return null;
  }

  const distinctId = posthog.get_distinct_id();
  return isValidPostHogDistinctId(distinctId) ? distinctId : null;
}

export function persistSignupDistinctIdCookie() {
  if (typeof document === 'undefined') {
    return;
  }
  if (!ensurePostHogInitialized() || posthog.get_property('$user_id')) {
    clearSignupDistinctIdCookie();
    return;
  }

  const distinctId = getCurrentPostHogDistinctId();
  if (!distinctId) {
    return;
  }

  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${POSTHOG_SIGNUP_DISTINCT_ID_COOKIE}=${encodeURIComponent(distinctId)}; ` +
    `Max-Age=${POSTHOG_SIGNUP_DISTINCT_ID_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secureAttribute}`;
}

export function clearSignupDistinctIdCookie() {
  if (typeof document === 'undefined') {
    return;
  }

  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${POSTHOG_SIGNUP_DISTINCT_ID_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute}`;
}
